<template>
	<div class="space-y-4">
		<!-- Filters - Full Width Above Both Sections -->
		<div class="bg-white rounded-lg border border-gray-200 p-3 space-y-4 mb-3">
			<!-- Group By -->
			<div>
				<div class="flex flex-wrap gap-2">
					<button
						v-for="option in groupByOptions"
						:key="option.value"
						:class="[
							'px-3 py-1.5 text-xs rounded-md border transition-all',
							groupBy === option.value
								? 'bg-mabey-600 text-white border-mabey-600'
								: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
						]"
						@click="groupBy = option.value">
						<font-awesome-icon :icon="option.icon" class="mr-1.5" />
						{{ option.label }}
					</button>
				</div>
			</div>

			<!-- Search and Filters -->
			<div class="flex flex-wrap gap-2 w-full">
				<div class="flex-1" style="min-width: 400px;">
					<div class="relative">
						<input
							v-model="searchFilter"
							type="text"
							placeholder="Search by contract, item code, description or depot..."
							class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400" />
						<font-awesome-icon icon="fas fa-search" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
					</div>
				</div>
				<div style="min-width: 180px;">
					<select
						v-model="dueInFilter"
						class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
						<option value="all">All Due Dates</option>
						<option
							v-for="dueDate in dueDateOptions"
							:key="dueDate"
							:value="dueDate">
							{{ formatShortDate(dueDate) }}
						</option>
					</select>
				</div>
				<div style="min-width: 180px;">
					<select
						v-model="targetDeliveryFilter"
						class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
						<option value="all">All Target Deliveries</option>
						<option value="missing">Missing Target Delivery</option>
						<option
							v-for="targetDeliveryDate in targetDeliveryDateOptions"
							:key="targetDeliveryDate"
							:value="targetDeliveryDate">
							{{ formatShortDate(targetDeliveryDate) }}
						</option>
					</select>
				</div>
			</div>

			<!-- Status Indicators and Clear Filters -->
			<div class="flex items-center justify-between">
				<div class="flex items-center space-x-4 text-xs">
					<div>
						<span class="text-gray-500">Items:</span>
						<span class="font-semibold ml-1">{{ flaggedItems.length }}</span>
					</div>
					<div v-if="itemsWithoutSource > 0">
						<span class="text-gray-500">No Source:</span>
						<span class="font-semibold ml-1 text-amber-600">{{ itemsWithoutSource }}</span>
					</div>
					<div>
						<span class="text-gray-500">Selected:</span>
						<span class="font-semibold ml-1 text-mabey-600">{{ selectedItemKeys.size }}</span>
					</div>
				</div>
				<div class="flex items-center space-x-2">
					<button
						v-if="searchFilter || dueInFilter !== 'all' || targetDeliveryFilter !== 'all'"
						class="px-2 py-1 text-xs font-medium text-red-700 hover:text-red-800 transition-colors flex items-center space-x-1 whitespace-nowrap"
						@click="clearFilters">
						<font-awesome-icon icon="fas fa-times" />
						<span>Clear Filters</span>
					</button>
				</div>
			</div>
		</div>
	</div>

		<!-- Warning for items without source depot -->
		<div v-if="itemsWithoutSource > 0" class="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
			<font-awesome-icon icon="fas fa-exclamation-triangle" class="text-amber-600 mt-0.5" />
			<div class="text-sm text-amber-800">
				<strong>{{ itemsWithoutSource }} item{{ itemsWithoutSource !== 1 ? 's' : '' }}</strong>
				{{ itemsWithoutSource !== 1 ? 'have' : 'has' }} no source depot allocated.
				These cannot be included in a TOS until a source depot is assigned in the Allocation tab.
			</div>
		</div>

		<!-- Grid for Items and Stock Check -->
		<div class="grid grid-cols-1 lg:grid-cols-12 gap-4 overflow-x-clip">
		<!-- Left Panel: Grouped Items -->
		<div class="lg:col-span-6 space-y-3 min-w-0">

			<!-- Empty State -->
			<div v-if="filteredGroups.length === 0 && requests.length > 0" class="bg-white rounded-lg border border-gray-200 p-12 text-center">
				<font-awesome-icon icon="fas fa-inbox" class="text-gray-300 text-5xl mb-4" />
				<h3 class="text-lg font-medium text-gray-900 mb-2">No items flagged for transfer</h3>
				<p class="text-gray-600">Items marked as "Flagged for Transfer" in the Allocation tab will appear here, ready for stock checking and TOS creation.</p>
			</div>

			<!-- Groups -->
			<div v-else class="space-y-3">
				<div v-for="group in filteredGroups" :key="group.key" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
					<!-- Group Header -->
					<div
						class="bg-gray-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between"
						@click="toggleGroup(group.key)">
						<div class="flex items-center space-x-3">
							<button class="text-gray-400 hover:text-gray-600 transition-colors">
								<svg class="w-4 h-4 transition-transform" :class="{'rotate-90': expandedGroups.has(group.key)}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
								</svg>
							</button>
							<div>
								<div class="flex items-center space-x-2">
									<span class="text-sm font-semibold text-gray-900">{{ group.label }}</span>
									<span v-if="group.sublabel" class="text-sm text-gray-500">{{ group.sublabel }}</span>
								</div>
								<div class="text-sm text-gray-500 mt-0.5">
									{{ group.items.length }} item{{ group.items.length !== 1 ? 's' : '' }}
									· {{ group.totalQuantity }} total qty
									<span v-if="group.totalWeight"> · {{ group.totalWeight.toFixed(3) }}t</span>
								</div>
							</div>
						</div>
						<div class="flex items-center space-x-3" @click.stop>
							<button
								:disabled="selectableItemsInGroup(group).length === 0"
								class="flex items-center space-x-1.5 px-2.5 py-1 text-xs rounded-md border transition-all"
								:class="isGroupFullySelected(group)
									? 'bg-mabey-600 text-white border-mabey-600'
									: selectableItemsInGroup(group).length === 0
										? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
										: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
								"
								@click="toggleGroupSelection(group)">
								<font-awesome-icon :icon="isGroupFullySelected(group) ? 'fas fa-check-square' : 'far fa-square'" />
								<span>{{ isGroupFullySelected(group) ? 'Deselect All' : 'Select All' }}</span>
							</button>
						</div>
					</div>

					<!-- Group Items -->
					<div v-if="expandedGroups.has(group.key)" class="divide-y divide-gray-100">
						<div
							v-for="item in group.items"
							:id="`item-${item.itemKey}`"
							:key="item.itemKey"
							class="px-4 py-3 text-sm hover:bg-gray-50 transition-colors cursor-pointer"
							:class="{
								'bg-mabey-50/30': selectedItemKeys.has(item.itemKey),
								'bg-amber-50/30': !item.sourceDepot
							}"
							@click="toggleItemSelection(item.itemKey)">
							<div class="flex items-start space-x-3">
								<!-- Checkbox -->
								<div class="pt-0.5">
									<button
										:disabled="!item.sourceDepot"
										:class="[
											'transition-colors',
											!item.sourceDepot
												? 'text-gray-300 cursor-not-allowed'
												: selectedItemKeys.has(item.itemKey)
													? 'text-mabey-600'
													: 'text-gray-400 hover:text-mabey-500'
										]"
										@click.stop="toggleItemSelection(item.itemKey)">
										<font-awesome-icon :icon="selectedItemKeys.has(item.itemKey) ? 'fas fa-check-square' : 'far fa-square'" class="text-lg" />
									</button>
								</div>

								<!-- Item Details -->
								<div class="flex-1 min-w-0">
									<!-- Item Header -->
									<div class="flex items-start justify-between mb-3">
										<div class="flex-1 min-w-0">
											<div class="flex items-center space-x-2">
												<button
													class="font-semibold text-mabey-600 hover:text-mabey-700 hover:underline transition-colors text-sm text-left"
													@click="emit('navigate-to-allocation', item.requestId, item.item.item_code)">
													{{ item.item.item_code }}
												</button>
												<span class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{{ item.requestContractNumber }}</span>
											</div>
											<div class="text-xs text-gray-600 mt-0.5 whitespace-normal leading-tight" :title="item.item.description">{{ item.item.description }}</div>
										</div>
										<div class="text-right ml-4 flex-shrink-0">
											<div class="font-bold text-gray-900 text-lg">{{ item.item.allocation_quantity || item.item.quantity_requested }}</div>
											<div class="text-xs text-gray-500">qty</div>
											<div v-if="item.item.item_weight" class="text-xs text-gray-400">{{ item.item.item_weight.toFixed(3) }}t</div>
										</div>
									</div>

									<!-- Main Details Grid -->
									<div class="rounded-lg p-3 mb-2">
										<div class="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2 text-xs">
											<!-- Column 1: Movement Info -->
											<div class="space-y-2">
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">From:</span>
													<span class="ml-3 font-medium" :class="item.sourceDepot ? 'text-gray-900' : 'text-amber-600'">
														{{ item.sourceDepot || 'Not allocated' }}
													</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">To:</span>
													<span class="ml-3 font-medium text-gray-900">{{ item.requestDepot }}</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">Due:</span>
													<span class="ml-3 font-medium" :class="getDaysClass(item.daysUntilDue)">
														{{ item.daysUntilDue >= 0 ? '+' : '' }}{{ item.daysUntilDue }}d
														<span class="text-gray-400 font-normal">({{ formatShortDate(item.item.required_by_date) }})</span>
													</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0 whitespace-nowrap">Target Delivery:</span>
													<span v-if="item.item.target_delivery_date" class="ml-3 font-medium text-gray-900">{{ formatShortDate(item.item.target_delivery_date) }}</span>
													<span v-else class="ml-3 font-medium text-red-600">Missing</span>
												</div>
											</div>
											
											<!-- Column 2: Item Details -->
											<div class="space-y-2">
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">Type:</span>
													<span class="ml-3 font-medium" :class="item.item.type === 'shortage' ? 'text-red-700' : 'text-green-700'">
														{{ item.item.type === 'shortage' ? 'Shortage' : 'Extra' }}
													</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">Required:</span>
													<span v-if="item.item.quantity_required !== null && item.item.quantity_required !== undefined" class="ml-3 font-medium text-gray-900">{{ item.item.quantity_required }}</span>
													<span v-else class="ml-3 font-medium text-red-600">Missing</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">Requested:</span>
													<span v-if="(item.item.allocation_quantity !== null && item.item.allocation_quantity !== undefined) || (item.item.quantity_requested !== null && item.item.quantity_requested !== undefined)" class="ml-3 font-medium text-gray-900">{{ item.item.allocation_quantity !== null && item.item.allocation_quantity !== undefined ? item.item.allocation_quantity : item.item.quantity_requested }}</span>
													<span v-else class="ml-3 font-medium text-red-600">Missing</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-20 flex-shrink-0">Weight:</span>
													<span v-if="item.item.item_weight" class="ml-3 font-medium text-gray-900">{{ item.item.item_weight }}t</span>
													<span v-else class="ml-3 font-medium text-gray-400">—</span>
												</div>
											</div>
											
											<!-- Column 3: Stock Check Info -->
											<div class="space-y-2">
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-28 flex-shrink-0">Stock Check Status:</span>
													<span v-if="item.item.stock_check_status === 'checked'" class="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
														<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Checked
													</span>
													<span v-else-if="item.item.stock_check_status === 'manually_checked'" class="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
														<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Manually Checked
													</span>
													<span v-else-if="item.item.stock_check_status === 'stock_check_pending'" class="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
														<font-awesome-icon icon="fas fa-clock" class="mr-1" /> Pending
													</span>
													<span v-else class="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
														<font-awesome-icon icon="fas fa-exclamation-circle" class="mr-1" /> Not Checked
													</span>
												</div>
												<div class="flex items-start">
													<span class="text-gray-500 font-medium w-28 flex-shrink-0">Stock Check Result:</span>
													<span v-if="item.item.stock_check_result" class="ml-3 font-medium" :class="item.item.stock_check_result >= (item.item.allocation_quantity || item.item.quantity_requested) ? 'text-green-700' : 'text-red-700 font-bold'">
														{{ item.item.stock_check_result }}
													</span>
													<span v-else class="ml-3 font-medium text-gray-400">—</span>
												</div>
											</div>
										</div>
									</div>

									<!-- Status Messages -->
									<div v-if="!item.sourceDepot" class="mt-2 flex items-center space-x-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded border border-amber-200">
										<font-awesome-icon icon="fas fa-exclamation-triangle" />
										<span>No source depot — </span>
										<button
											class="text-amber-800 underline hover:text-amber-900 font-medium"
											@click="emit('navigate-to-allocation', item.requestId, item.item.item_code)">
											assign in Allocation tab
										</button>
										<span> to include in TOS</span>
									</div>

									<div v-if="item.item.tos_no" class="mt-2 flex items-center space-x-2 text-xs text-blue-700 bg-blue-50 px-3 py-2 rounded border border-blue-200">
										<font-awesome-icon icon="fas fa-info-circle" />
										<span>Already assigned to TOS {{ item.item.tos_no }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Right Panel: Stock Check + TOS Builder -->
		<div class="lg:col-span-6 space-y-4 overflow-hidden">
			<!-- Stock Check -->
			<div v-if="sourceDepotGroups.length > 0" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
				<div class="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
					<div class="flex items-center space-x-2">
						<font-awesome-icon icon="fas fa-clipboard-check" class="text-mabey-600" />
						<span class="text-sm font-semibold text-gray-900">Stock Check</span>
						<span class="text-xs text-gray-500">({{ sourceDepotGroups.length }} depot{{ sourceDepotGroups.length !== 1 ? 's' : '' }})</span>
					</div>
					<div class="flex items-center space-x-2">
						<button
							class="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
							@click="toggleAllDepots">
							<font-awesome-icon :icon="allDepotsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="mr-1" />
							{{ allDepotsExpanded ? 'Collapse All' : 'Expand All' }}
						</button>
						<button
							:disabled="checkingDepots.size > 0"
							class="px-3 py-1 text-xs font-medium rounded-md border transition-all flex items-center space-x-1.5"
							:class="checkingDepots.size > 0
								? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
								: 'bg-mabey-600 text-white border-mabey-600 hover:bg-mabey-700'
							"
							@click="showCheckAllModal = true">
							<font-awesome-icon :icon="checkingDepots.size > 0 ? 'fas fa-spinner fa-spin' : 'fas fa-check'" />
							<span>Check All Depots</span>
						</button>
					</div>
				</div>
				<div class="divide-y divide-gray-100">
					<div v-for="depot in sourceDepotGroups" :key="depot.depotName">
						<!-- Depot Header -->
						<div class="px-4 py-2.5 hover:bg-gray-50 transition-colors">
							<div class="flex items-center justify-between">
								<div class="flex items-center space-x-3 flex-1 cursor-pointer" @click="toggleDepot(depot.depotName)">
									<button class="text-gray-400 hover:text-gray-600 transition-colors">
										<svg class="w-4 h-4 transition-transform" :class="{'rotate-90': expandedDepots.has(depot.depotName)}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
										</svg>
									</button>
									<div class="flex items-center space-x-2">
										<font-awesome-icon icon="fas fa-warehouse" class="text-gray-400 text-xs" />
										<span class="text-sm font-semibold text-gray-900">{{ depot.depotName }}</span>
									</div>
									<span class="text-xs text-gray-500">{{ depot.itemCount }} item{{ depot.itemCount !== 1 ? 's' : '' }}</span>
								</div>
								<div class="flex items-center space-x-2" @click.stop>
									<!-- Status Badge -->
									<span v-if="depotOverallStockCheckStatus.get(depot.depotName) === 'checked'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800">
										<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Checked
									</span>
									<span v-else-if="depotOverallStockCheckStatus.get(depot.depotName) === 'pending'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
										<font-awesome-icon icon="fas fa-clock" class="mr-1" /> In Progress
									</span>
									<span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
										<font-awesome-icon icon="fas fa-clock" class="mr-1" /> Awaiting
									</span>
									<!-- Action Button -->
									<button
										:disabled="checkingDepots.has(depot.depotName)"
										class="px-3 py-1.5 text-xs font-medium rounded-md border transition-all flex items-center space-x-1.5"
										:class="checkingDepots.has(depot.depotName)
											? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
											: depot.status
												? 'bg-white text-gray-600 border-gray-300 hover:border-mabey-400 hover:text-mabey-700'
												: 'bg-white text-mabey-700 border-mabey-300 hover:bg-mabey-50 hover:border-mabey-400'
										"
										@click="runStockCheckForDepot(depot.depotName)">
										<font-awesome-icon :icon="checkingDepots.has(depot.depotName) ? 'fas fa-spinner fa-spin' : 'fas fa-clipboard-check'" />
										<span>{{ checkingDepots.has(depot.depotName) ? 'Updating...' : 'Request Check' }}</span>
									</button>
								</div>
							</div>
						</div>
						
						<!-- Depot Items (Expandable) -->
						<div v-if="expandedDepots.has(depot.depotName)" class="bg-gray-50 px-4 py-3 overflow-x-auto max-w-full">
							<table class="w-full text-xs stock-check-table min-w-[500px]">
								<thead>
									<tr class="text-left text-gray-600 border-b border-gray-300">
										<th class="pb-2 font-semibold">Item Code</th>
										<th class="pb-2 font-semibold">Description</th>
										<th class="pb-2 font-semibold text-center">Qty</th>
										<th class="pb-2 font-semibold text-center">Result</th>
										<th class="pb-2 font-semibold text-right">Stock Check Status</th>
										<th class="pb-2 font-semibold text-right">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr
v-for="item in depot.items" :key="item.itemKey" 
										class="border-b border-gray-200 hover:bg-white/50 transition-colors"
										:class="{
											'bg-green-50': item.item.stock_check_status === 'checked' || item.item.stock_check_status === 'manually_checked',
											'bg-amber-50': item.item.stock_check_status === 'stock_check_pending',
											'bg-red-50': !item.item.stock_check_status || item.item.stock_check_status === 'not_checked'
										}">
										<td class="py-2 font-medium truncate" :title="item.item.item_code">
											<button
												class="text-mabey-600 hover:text-mabey-700 hover:underline transition-colors text-left"
												@click="emit('navigate-to-allocation', item.requestId, item.item.item_code)">
												{{ item.item.item_code }}
											</button>
										</td>
										<td class="py-2 text-gray-700 truncate" :title="item.item.description">{{ item.item.description }}</td>
										<td class="py-2 text-gray-900 font-semibold text-center">{{ item.item.allocation_quantity || item.item.quantity_requested }}</td>
										<td class="py-2 text-center">
											<span
												v-if="item.item.stock_check_result"
												class="font-semibold"
												:class="item.item.stock_check_result >= (item.item.allocation_quantity || item.item.quantity_requested) ? 'text-green-700' : 'text-red-700 font-bold'">
												{{ item.item.stock_check_result }}
											</span>
											<span v-else class="text-gray-400">—</span>
										</td>
										<td class="py-2 text-right">
											<span v-if="item.item.stock_check_status === 'checked'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800">
												<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Checked
											</span>
											<span v-else-if="item.item.stock_check_status === 'manually_checked'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800">
												<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Manually Checked
											</span>
											<span v-else-if="item.item.stock_check_status === 'stock_check_pending'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
												<font-awesome-icon icon="fas fa-clock" class="mr-1" /> Pending
											</span>
											<span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800">
												<font-awesome-icon icon="fas fa-exclamation-circle" class="mr-1" /> Not Checked
											</span>
										</td>
										<td class="py-2 text-right">
											<button
												v-if="item.item.stock_check_status !== 'checked' && item.item.stock_check_status !== 'manually_checked'"
												:disabled="savingItems.has(item.item.id!)"
												class="px-2 py-1 text-[10px] font-medium rounded-md border transition-all inline-flex items-center space-x-1 whitespace-nowrap"
												:class="savingItems.has(item.item.id!)
													? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
													: 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
												"
												title="Mark as manually checked"
												@click.stop="markItemManuallyChecked(item)">
												<font-awesome-icon :icon="savingItems.has(item.item.id!) ? 'fas fa-spinner fa-spin' : 'fas fa-user-check'" />
												<span>Manually Checked</span>
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			<!-- TOS Builder -->
			<div class="bg-white rounded-lg border border-gray-200 overflow-hidden sticky top-4">
				<div class="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
					<h3 class="text-sm font-semibold text-gray-900">
						<font-awesome-icon icon="fas fa-check-circle" class="mr-1.5 text-mabey-600" />
						TOS Builder
					</h3>
					<button
						class="px-3 py-1 bg-white text-gray-700 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
						@click="clearSelection">
						Clear All
					</button>
				</div>

				<div class="p-4">
					<!-- Empty state -->
					<div v-if="selectedItemKeys.size === 0" class="text-center py-8">
												<p class="text-sm text-gray-500 mb-1">No items selected</p>
						<p class="text-xs text-gray-400">Select items from the left to build TOS groupings by route</p>
					</div>

					<!-- TOS Builder Content -->
					<div v-else class="space-y-3">
						<!-- Overall Stats -->
						<div class="grid grid-cols-8 gap-2">
							<div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
								<div class="text-gray-600 font-medium mb-1">Items</div>
								<div class="font-bold text-gray-900 text-lg">{{ selectedItemKeys.size }}</div>
							</div>
							<div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
								<div class="text-gray-600 font-medium mb-1">Routes</div>
								<div class="font-bold text-gray-900 text-lg">{{ selectedRouteGroups.length }}</div>
							</div>
							<div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
								<div class="text-gray-600 font-medium mb-1">Total Qty</div>
								<div class="font-bold text-gray-900 text-lg">{{ selectedTotalQuantity }}</div>
							</div>
							<div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
								<div class="text-gray-600 font-medium mb-1">Weight</div>
								<div class="font-bold text-gray-900 text-lg">{{ selectedTotalWeight > 0 ? selectedTotalWeight.toFixed(2) + 't' : '—' }}</div>
							</div>
						</div>

						<!-- Associated Shortage Requests -->
						<div v-if="selectedContractGroups.length > 0" class="bg-gray-50 rounded-lg p-3 border border-gray-200">
							<div class="flex items-center space-x-2 mb-3">
								<font-awesome-icon icon="fas fa-file-contract" class="text-mabey-600 text-xs" />
								<span class="text-xs font-semibold text-gray-700">Associated Shortage Requests</span>
								<span class="text-xs text-gray-500">({{ selectedContractGroups.length }})</span>
							</div>
							<div class="space-y-2">
								<div
									v-for="contract in selectedContractGroups"
									:key="contract.contract_number"
									class="bg-white rounded-lg p-2.5 border border-gray-200">
									<div class="flex items-center justify-between mb-2">
										<div class="flex items-center space-x-2">
											<span class="text-xs font-semibold text-gray-900">{{ contract.contract_number }}</span>
											<span
class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium capitalize"
												:class="getRequestStatusClass(getRequestStatus(contract.shortage_request_id))">
												{{ getRequestStatus(contract.shortage_request_id) }}
											</span>
											<span v-if="contract.fulfillment_percentage === 100" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-800">
												<font-awesome-icon icon="fas fa-check-circle" class="mr-0.5" /> Complete
											</span>
										</div>
										<button
											class="text-xs text-mabey-600 hover:text-mabey-700 font-medium flex items-center space-x-1 transition-colors"
											@click="viewShortageRequest(contract.shortage_request_id)">
											<font-awesome-icon icon="fas fa-external-link-alt" />
											<span>View</span>
										</button>
									</div>
									<div class="space-y-1">
										<div class="flex items-center justify-between text-[11px]">
											<span class="text-gray-600">
												<span class="font-semibold text-gray-900">{{ contract.covered_after_tos }}</span> of 
												<span class="font-semibold text-gray-900">{{ contract.total_items }}</span> items covered after this TOS
											</span>
										</div>
										<div class="flex items-center justify-between text-[11px] mb-1">
											<span class="text-gray-500">Overall fulfillment</span>
											<span class="font-semibold" :class="contract.fulfillment_percentage === 100 ? 'text-green-600' : contract.fulfillment_percentage >= 50 ? 'text-blue-600' : 'text-amber-600'">
												{{ contract.fulfillment_percentage }}%
											</span>
										</div>
										<div class="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
											<div
												class="h-full transition-all duration-300 rounded-full"
												:class="contract.fulfillment_percentage === 100 ? 'bg-green-500' : contract.fulfillment_percentage >= 50 ? 'bg-blue-500' : 'bg-amber-500'"
												:style="{ width: contract.fulfillment_percentage + '%' }"></div>
										</div>
										<div v-if="contract.fulfillment_percentage < 100" class="text-[10px] text-gray-500 italic mt-1">
											Additional TOS may be needed to complete this request
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Route Groupings Preview -->
						<div class="space-y-2">
							<div class="space-y-2">
								<div v-for="route in selectedRouteGroups" :key="route.routeKey" class="border border-gray-200 rounded-lg overflow-hidden bg-white">
									<!-- Route Header -->
									<div class="bg-gray-50 px-3 py-2.5 border-b border-gray-200">
										<div class="flex items-center justify-between mb-2">
											<div class="flex items-center space-x-2 text-sm">
												<span class="font-semibold text-gray-900">{{ route.sourceDepot }}</span>
												<font-awesome-icon icon="fas fa-circle-arrow-right" class="text-mabey-500" />
												<span class="font-semibold text-gray-900">{{ route.destinationDepot }}</span>
											</div>
											<div class="flex items-center space-x-2">
												<button
													class="px-2 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center space-x-1"
													@click.stop="openCreateTosModal(route)">
													<font-awesome-icon icon="fas fa-plus" class="text-xs" />
													<span>Create TOS</span>
												</button>
											</div>
										</div>
										<!-- Route Stats -->
										<div class="flex items-center gap-3 mb-2">
											<div class="bg-white/60 rounded px-2 py-1 border border-gray-300">
												<div class="text-[10px] text-gray-600 font-medium">Items</div>
												<div class="text-sm font-bold text-gray-900">{{ route.items.length }}</div>
											</div>
											<div class="bg-white/60 rounded px-2 py-1 border border-gray-300">
												<div class="text-[10px] text-gray-600 font-medium">Total Qty</div>
												<div class="text-sm font-bold text-gray-900">{{ route.totalQuantity }}</div>
											</div>
 										<div v-if="route.totalWeight > 0" class="bg-white/60 rounded px-2 py-1 border border-gray-300">
												<div class="text-[10px] text-gray-600 font-medium">Weight</div>
												<div class="text-sm font-bold text-gray-900">{{ route.totalWeight.toFixed(2) }}t</div>
											</div>
										</div>
									</div>
									<!-- Route Items -->
									<div class="bg-gray-50 px-3 py-3 overflow-hidden max-w-full">
										<!-- Top Scrollbar -->
										<div
											ref="topScrollContainer"
											class="overflow-x-auto overflow-y-hidden w-full border-b border-gray-100"
											@scroll="syncScroll(topScrollContainer, tableContainer)">
											<div class="h-px" style="min-width: 800px"></div>
										</div>

										<div
											ref="tableContainer"
											class="overflow-x-auto overflow-y-hidden w-full"
											@scroll="syncScroll(tableContainer, topScrollContainer)">
											<table class="w-full text-xs min-w-[800px]">
											<thead>
												<tr class="text-left text-gray-600 border-b border-gray-300">
													<th class="p-2 font-semibold">Item Code</th>
													<th class="p-2 font-semibold text-center">Weight</th>
													<th class="p-2 font-semibold">Due Date</th>
													<th class="p-2 font-semibold">Target Delivery</th>
													<th class="p-2 font-semibold text-right">Qty</th>
													<th class="p-2 font-semibold text-right">Result</th>
													<th class="p-2 font-semibold text-right">Stock Check</th>
												</tr>
											</thead>
											<tbody>
												<tr
v-for="rItem in route.items" :key="rItem.itemKey" 
													class="border-b border-gray-200 hover:bg-white/50 transition-colors"
													:class="{
														'bg-green-50': rItem.item.stock_check_status === 'checked' || rItem.item.stock_check_status === 'manually_checked',
														'bg-amber-50': rItem.item.stock_check_status === 'stock_check_pending',
														'bg-red-50': !rItem.item.stock_check_status || rItem.item.stock_check_status === 'not_checked'
													}">
   										<td class="p-2 font-medium">
   											<button
   												class="text-mabey-600 hover:text-mabey-700 hover:underline transition-colors text-left"
   												@click="emit('navigate-to-allocation', rItem.requestId, rItem.item.item_code)">
   												{{ rItem.item.item_code }}
   											</button>
   										</td>
													<td class="p-2 text-gray-700 text-center">{{ rItem.item.item_weight ? rItem.item.item_weight.toFixed(3) + 't' : '—' }}</td>
													<td class="p-2 text-gray-700">{{ formatShortDate(rItem.item.required_by_date) }}</td>
													<td class="p-2">
														<span v-if="rItem.item.target_delivery_date" class="text-gray-700">{{ formatShortDate(rItem.item.target_delivery_date) }}</span>
														<span v-else class="text-red-600 font-semibold">Missing</span>
													</td>
													<td class="p-2 text-gray-900 font-semibold text-right">{{ rItem.item.allocation_quantity }}</td>
													<td class="p-2 text-right">
														<span
															v-if="rItem.item.stock_check_result !== null && rItem.item.stock_check_result !== undefined"
															class="font-semibold"
															:class="rItem.item.stock_check_result >= (rItem.item.allocation_quantity || rItem.item.quantity_requested) ? 'text-green-700' : 'text-red-700 font-bold'">
															{{ rItem.item.stock_check_result }}
														</span>
														<span v-else class="text-gray-400">—</span>
													</td>
													<td class="p-2 text-right">
														<span v-if="rItem.item.stock_check_status === 'checked'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800">
															<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Checked
														</span>
														<span v-else-if="rItem.item.stock_check_status === 'manually_checked'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800">
															<font-awesome-icon icon="fas fa-user-check" class="mr-1" /> Manual
														</span>
														<span v-else-if="rItem.item.stock_check_status === 'stock_check_pending'" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
															<font-awesome-icon icon="fas fa-clock" class="mr-1" /> Pending
														</span>
														<span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800">
															<font-awesome-icon icon="fas fa-exclamation-circle" class="mr-1" /> Not Checked
														</span>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<!-- Route Footer with Contracts -->
									<div v-if="route.contracts.length > 0" class="px-3 py-2 bg-white border-t border-gray-200">
										<div class="text-xs text-gray-500">
											<span class="font-medium">Contracts:</span>
											<span class="ml-1">{{ route.contracts.join(', ') }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		</div>
	</div>

	<!-- Check All Depots Confirmation Modal -->
	<teleport to="body">
		<div v-if="showCheckAllModal" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="showCheckAllModal = false"></div>
			<div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
				<div class="px-6 py-4 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900">Re-request All Stock Checks</h2>
				</div>
				<div class="p-6">
					<div class="flex items-start space-x-3">
						<font-awesome-icon icon="fas fa-exclamation-triangle" class="text-amber-500 text-xl mt-0.5" />
						<div>
							<p class="text-sm text-gray-700 mb-2">This will re-submit <strong>{{ totalItemsToReset }} items across all depots</strong> for checking, are you sure?</p>
							<p class="text-sm text-gray-500">Any existing stock check results will need to be re-confirmed on the Stock Check Requests page.</p>
						</div>
					</div>
				</div>
				<div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
					<button
						class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
						@click="showCheckAllModal = false">
						Cancel
					</button>
					<button
						class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-1.5"
						@click="confirmCheckAllDepots">
						<span>Continue</span>
					</button>
				</div>
			</div>
		</div>
	</teleport>

	<!-- Individual Depot Stock Check Modal -->
	<teleport to="body">
		<div v-if="depotToReset" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="depotToReset = null"></div>
			<div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
				<div class="px-6 py-4 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900">Re-request Stock Check</h2>
				</div>
				<div class="p-6">
					<div class="flex items-start space-x-3">
						<font-awesome-icon icon="fas fa-exclamation-triangle" class="text-amber-500 text-xl mt-0.5" />
						<div>
							<p class="text-sm text-gray-700 mb-2">This will re-submit <strong>{{ itemsInDepotToReset.length }} items</strong> at <strong>{{ depotToReset }}</strong> for checking, are you sure?</p>
							<p class="text-sm text-gray-500">Any existing stock check results for this depot will need to be re-confirmed.</p>
						</div>
					</div>
				</div>
				<div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
					<button
						class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
						@click="depotToReset = null">
						Cancel
					</button>
					<button
						class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-1.5"
						@click="confirmDepotReset">
						<font-awesome-icon icon="fas fa-rotate-left" />
						<span>Continue</span>
					</button>
				</div>
			</div>
		</div>
	</teleport>

	<!-- Simple Create TOS Modal -->
	<teleport to="body">
		<div v-if="showTosModal && tosPayload && selectedRouteForReview" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="closeModal"></div>
			<div class="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
				<!-- Modal Header -->
				<div class="px-6 py-4 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900">Create Transfer of Stock</h2>
					<div class="flex items-center space-x-2 mt-1 text-sm text-gray-600">
						<span class="px-2 py-0.5 bg-gray-100 rounded border font-semibold">{{ tosPayload.source_depot }}</span>
						<font-awesome-icon icon="fas fa-circle-arrow-right" class="text-mabey-500" />
						<span class="px-2 py-0.5 bg-gray-100 rounded border font-semibold">{{ tosPayload.destination_depot }}</span>
					</div>
				</div>

				<!-- Modal Content -->
				<div class="p-6">
					<!-- Summary Stats -->
					<div class="grid grid-cols-3 gap-3 mb-4">
						<div class="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
							<div class="text-xs text-gray-600 font-medium mb-1">Items</div>
							<div class="text-xl font-bold text-gray-900">{{ tosPayload.items.length }}</div>
						</div>
						<div class="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
							<div class="text-xs text-gray-600 font-medium mb-1">Total Qty</div>
							<div class="text-xl font-bold text-gray-900">{{ tosPayload.total_quantity }}</div>
						</div>
						<div class="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
							<div class="text-xs text-gray-600 font-medium mb-1">Weight</div>
							<div class="text-xl font-bold text-gray-900">{{ tosPayload.total_weight ? tosPayload.total_weight.toFixed(2) + 't' : '—' }}</div>
						</div>
					</div>

					<!-- Notes Field -->
					<div class="bg-white rounded-lg p-3 border border-gray-200 mb-4">
						<label class="block text-sm font-semibold text-gray-700 mb-2">Add a note (optional)</label>
						<textarea
							v-model="userNotes"
							rows="3"
							placeholder="Add any additional notes or instructions for this transfer..."
							class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 resize-none"></textarea>
					</div>

					<!-- Validation Warnings (In Modal) -->
					<div v-if="getValidationWarnings(selectedRouteForReview).length > 0" class="space-y-2">
						<div class="text-sm font-semibold text-amber-800 flex items-center space-x-2">
							<font-awesome-icon icon="fas fa-exclamation-triangle" class="text-amber-600" />
							<span>Validation Warnings ({{ getValidationWarnings(selectedRouteForReview).length }})</span>
						</div>
						<div class="bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
							<div v-for="(warning, idx) in getValidationWarnings(selectedRouteForReview)" :key="idx" class="text-xs">
								<div class="text-amber-800 font-medium">• {{ warning.message }}</div>
								<div v-if="warning.items && warning.items.length > 0" class="flex flex-wrap gap-1 mt-1 ml-3">
									<button
										v-for="item in warning.items"
										:key="item.itemKey"
										class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 hover:border-amber-400 transition-colors"
										@click="closeModal(); emit('navigate-to-allocation', item.requestId, item.item.item_code)">
										{{ item.item.item_code }}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Modal Footer -->
				<div class="border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-2">
					<button
						:disabled="creatingTos"
						class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						@click="closeModal">
						Cancel
					</button>
					<button
						:disabled="creatingTos"
						class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
						@click="createTos">
						<app-loader v-if="creatingTos" size="small" class="w-4 h-4" />
						<font-awesome-icon v-else icon="fas fa-check" />
						<span>{{ creatingTos ? 'Creating TOS...' : 'Confirm' }}</span>
					</button>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script lang="ts" setup>
	import { ref, computed, watch } from 'vue'
	import type { StockShortageResponse, StockShortageItem, StockCheckResult, TransferOfStockPayload, TransferOfStockItem } from '@/data/schema/stock-shortage'
	import StockService from '@/services/stock'
	import useToaster from '@/stores/toaster'
	import AppLoader from '@/components/base/AppLoader.vue'

	interface FlaggedItem {
		item: StockShortageItem
		requestId: string
		requestDepot: string
		requestContractNumber: string
		requestStatus: string
		requestedBy: string
		itemKey: string
		daysUntilDue: number
		sourceDepot: string | null
		stockCheckResult: StockCheckResult | null
	}

	interface ItemGroup {
		key: string
		label: string
		sublabel?: string
		items: FlaggedItem[]
		totalQuantity: number
		totalWeight: number
		sourceDepot?: string
	}

	interface SelectedRouteGroup {
		routeKey: string
		sourceDepot: string
		destinationDepot: string
		items: FlaggedItem[]
		totalQuantity: number
		totalWeight: number
		contracts: string[]
		stockCheckStatus: 'passed' | 'failed' | 'partial' | null
	}

	type GroupByMode = 'route' | 'destination' | 'source' | 'contract'

	const props = defineProps<{
		requests: StockShortageResponse[]
	}>()

	const emit = defineEmits<{
		updated: [request?: StockShortageResponse]
		'focus-request': [requestId: string]
		'navigate-to-allocation': [requestId: string, itemCode: string]
	}>()

	// Stock service and toaster
	const stockService = new StockService()
	const toaster = useToaster()

	// Grouping & selection state
	const groupBy = ref<GroupByMode>('route')
	const searchFilter = ref('')
	const dueInFilter = ref<string>('all')
	const targetDeliveryFilter = ref<string>('all')
	
	// Load saved selections from localStorage
	const loadSavedSelections = (): Set<string> => {
		try {
			const saved = localStorage.getItem('tosBuilderSelections')
			if (saved) {
				return new Set(JSON.parse(saved))
			}
		} catch (error) {
			console.error('Error loading saved selections:', error)
		}
		return new Set()
	}
	
	const selectedItemKeys = ref<Set<string>>(loadSavedSelections())

	const getDueDateValue = (dateString: string): string => {
		if (!dateString) return ''
		const directMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/)
		if (directMatch) return directMatch[1]

		const date = new Date(dateString)
		if (Number.isNaN(date.getTime())) return ''

		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	const dueDateOptions = computed(() => {
		const dueDates = new Set<string>()
		for (const item of flaggedItems.value) {
			const dueDate = getDueDateValue(item.item.required_by_date)
			if (dueDate) {
				dueDates.add(dueDate)
			}
		}
		return Array.from(dueDates).sort((a, b) => a.localeCompare(b))
	})

	const targetDeliveryDateOptions = computed(() => {
		const targetDeliveryDates = new Set<string>()
		for (const item of flaggedItems.value) {
			const targetDeliveryDate = getDueDateValue(item.item.target_delivery_date || '')
			if (targetDeliveryDate) {
				targetDeliveryDates.add(targetDeliveryDate)
			}
		}
		return Array.from(targetDeliveryDates).sort((a, b) => a.localeCompare(b))
	})

	watch(() => props.requests, (newRequests, oldRequests) => {
		const requestsChanged = !oldRequests || 
			newRequests.length !== oldRequests.length ||
			newRequests.some((req, idx) => !oldRequests[idx] || req.id !== oldRequests[idx].id)
		
		if (requestsChanged) {
			selectedItemKeys.value.clear()
			expandedGroups.value.clear()
			localStorage.removeItem('tosBuilderSelections')
		}
	}, { deep: true })

	// Watch for item status changes to update flagged items count
	watch(() => props.requests, () => {
		// Force reactivity for flagged items when individual items change
		// This ensures counts update when fulfillment_status changes in Stock Sourcing
	}, { deep: true })

	const expandedGroups = ref<Set<string>>(new Set())

	// Stock check state (depot-level)
	const savingItems = ref<Set<string>>(new Set())
	const checkingDepots = ref<Set<string>>(new Set())
	const expandedDepots = ref<Set<string>>(new Set())

	const topScrollContainer = ref<HTMLElement | null>(null)
	const tableContainer = ref<HTMLElement | null>(null)

	const syncScroll = (source: HTMLElement | null, target: HTMLElement | null) => {
		if (source && target) {
			target.scrollLeft = source.scrollLeft
		}
	}

	const stockCheckResults = ref<Map<string, StockCheckResult>>(new Map())
	const depotStockCheckStatus = ref<Map<string, 'passed' | 'failed' | 'partial'>>(new Map())

	// Computed directly from flaggedItems to avoid circular dependency with sourceDepotGroups
	const depotOverallStockCheckStatus = computed(() => {
		const statusMap = new Map<string, 'awaiting' | 'pending' | 'checked'>()

		const depotMap = new Map<string, string[]>()
		for (const item of flaggedItems.value) {
			if (!item.sourceDepot) continue
			if (!depotMap.has(item.sourceDepot)) depotMap.set(item.sourceDepot, [])
			depotMap.get(item.sourceDepot)!.push(item.item?.stock_check_status || 'not_checked')
		}

		for (const [depotName, statuses] of depotMap.entries()) {
			const uniqueStatuses = [...new Set(statuses)]
			if (uniqueStatuses.length === 1 && uniqueStatuses[0] === 'not_checked') {
				statusMap.set(depotName, 'awaiting')
			} else if (uniqueStatuses.every(s => s === 'checked' || s === 'manually_checked')) {
				statusMap.set(depotName, 'checked')
			} else {
				statusMap.set(depotName, 'pending')
			}
		}

		return statusMap
	})

	// Check All Depots modal state
	const showCheckAllModal = ref(false)
	const depotToReset = ref<string | null>(null)
	const itemsInDepotToReset = computed(() => {
		if (!depotToReset.value) return []
		return flaggedItems.value.filter(i => i.sourceDepot === depotToReset.value)
	})
	const totalItemsToReset = computed(() => {
		return sourceDepotGroups.value.reduce((sum, g) => sum + g.items.length, 0)
	})

	// TOS modal state
	const showTosModal = ref(false)
	const selectedRouteForReview = ref<SelectedRouteGroup | null>(null)
	const tosPayload = ref<TransferOfStockPayload | null>(null)
	const userNotes = ref('')
	const creatingTos = ref(false)
	
	const groupByOptions: { value: GroupByMode; label: string; icon: string }[] = [
		{ value: 'route', label: 'Route (From → To)', icon: 'fas fa-route' },
		{ value: 'destination', label: 'Destination Depot', icon: 'fas fa-truck-arrow-right' },
		{ value: 'source', label: 'Source Depot', icon: 'fas fa-warehouse' },
		{ value: 'contract', label: 'Contract', icon: 'fas fa-clipboard' }
	]

	// Extract all flagged-for-transfer items with full context
	const flaggedItems = computed((): FlaggedItem[] => {
		const result: FlaggedItem[] = []
		const now = Date.now()

		for (const req of props.requests) {
			req.items.forEach((item, index) => {
				if (item.fulfillment_status !== 'flagged-for-transfer') return

				const daysUntilDue = Math.ceil((new Date(item.required_by_date).getTime() - now) / 86400000)
				const itemKey = `${req.id}_${index}`
				const checkKey = `${item.allocation_from_depot}|${item.item_code}|${itemKey}`

				result.push({
					item,
					requestId: req.id,
					requestDepot: req.depot,
					requestContractNumber: req.contract_number,
					requestStatus: req.status,
					requestedBy: req.requested_by,
					itemKey,
					daysUntilDue,
					sourceDepot: item.allocation_from_depot || null,
					stockCheckResult: stockCheckResults.value.get(checkKey) || item.stock_check_result || null
				})
			})
		}

		return result
	})

	const itemsWithoutSource = computed(() => {
		return flaggedItems.value.filter(i => !i.sourceDepot).length
	})

	// Source depot groups for the stock check bar
	interface SourceDepotGroup {
		depotName: string
		itemCount: number
		destinationCount: number
		items: FlaggedItem[]
		status: 'passed' | 'failed' | 'partial' | null
	}

	const sourceDepotGroups = computed((): SourceDepotGroup[] => {
		const depotMap = new Map<string, FlaggedItem[]>()

		// Use filteredFlaggedItems instead of flaggedItems to make stock check dynamic
		for (const item of filteredFlaggedItems.value) {
			if (!item.sourceDepot) continue
			if (!depotMap.has(item.sourceDepot)) {
				depotMap.set(item.sourceDepot, [])
			}
			depotMap.get(item.sourceDepot)!.push(item)
		}

		const groups: SourceDepotGroup[] = []
		for (const [depotName, items] of depotMap.entries()) {
			const destinations = new Set(items.map(i => i.requestDepot))
			groups.push({
				depotName,
				itemCount: items.length,
				destinationCount: destinations.size,
				items,
				status: depotStockCheckStatus.value.get(depotName) || null
			})
		}

		return groups.sort((a, b) => a.depotName.localeCompare(b.depotName))
	})

	// Apply search filter
	const filteredFlaggedItems = computed((): FlaggedItem[] => {
		return flaggedItems.value.filter(item => {
			// Apply search filter
			if (searchFilter.value) {
				const search = searchFilter.value.toLowerCase()
				const matchesSearch = 
					item.requestContractNumber.toLowerCase().includes(search) ||
					item.item.item_code.toLowerCase().includes(search) ||
					item.item.description.toLowerCase().includes(search) ||
					item.requestDepot.toLowerCase().includes(search) ||
					(item.sourceDepot && item.sourceDepot.toLowerCase().includes(search)) ||
					item.requestedBy.toLowerCase().includes(search)
				if (!matchesSearch) return false
			}
			
			// Apply due date filter
			if (dueInFilter.value !== 'all') {
				const dueDate = getDueDateValue(item.item.required_by_date)
				if (dueDate !== dueInFilter.value) return false
			}
			
			// Apply target delivery filter
			if (targetDeliveryFilter.value !== 'all') {
				if (!item.item.target_delivery_date) {
					// If no target delivery date, only show in 'missing' filter
					if (targetDeliveryFilter.value !== 'missing') return false
				} else {
					if (targetDeliveryFilter.value === 'missing') return false

					const targetDeliveryDate = getDueDateValue(item.item.target_delivery_date)
					if (targetDeliveryDate !== targetDeliveryFilter.value) return false
				}
			}
			
			return true
		})
	})

	// Group items based on selected mode
	const filteredGroups = computed((): ItemGroup[] => {
		const items = filteredFlaggedItems.value
		const groupMap = new Map<string, FlaggedItem[]>()

		for (const item of items) {
			let key: string
			switch (groupBy.value) {
				case 'route':
					key = `${item.sourceDepot || 'UNALLOCATED'} → ${item.requestDepot}`
					break
				case 'destination':
					key = item.requestDepot
					break
				case 'source':
					key = item.sourceDepot || 'UNALLOCATED'
					break
				case 'contract':
					key = item.requestContractNumber
					break
			}

			if (!groupMap.has(key)) {
				groupMap.set(key, [])
			}
			groupMap.get(key)!.push(item)
		}

		const groups: ItemGroup[] = []

		for (const [key, groupItems] of groupMap.entries()) {
			let label = key
			let sublabel: string | undefined
			let sourceDepot: string | undefined

			switch (groupBy.value) {
				case 'route':
					label = key
					sublabel = `${groupItems.length} item${groupItems.length !== 1 ? 's' : ''} on this route`
					sourceDepot = groupItems[0]?.sourceDepot || undefined
					break
				case 'destination':
					label = `To: ${key}`
					sublabel = `from ${new Set(groupItems.map(i => i.sourceDepot || 'unallocated')).size} source depot(s)`
					break
				case 'source':
					label = key === 'UNALLOCATED' ? 'No Source Depot' : `From: ${key}`
					sublabel = `to ${new Set(groupItems.map(i => i.requestDepot)).size} destination(s)`
					sourceDepot = key !== 'UNALLOCATED' ? key : undefined
					break
				case 'contract':
					label = `Contract: ${key}`
					sublabel = `${new Set(groupItems.map(i => i.requestDepot)).size} depot(s)`
					break
			}

			const totalQuantity = groupItems.reduce((sum, i) => sum + (i.item.allocation_quantity || i.item.quantity_requested), 0)
			const totalWeight = groupItems.reduce((sum, i) => sum + (i.item.item_weight || 0), 0)

			groups.push({
				key,
				label,
				sublabel,
				items: groupItems.sort((a, b) => a.daysUntilDue - b.daysUntilDue),
				totalQuantity,
				totalWeight,
				sourceDepot
			})
		}

		return groups.sort((a, b) => {
			if (a.key === 'UNALLOCATED' || a.key.startsWith('UNALLOCATED')) return 1
			if (b.key === 'UNALLOCATED' || b.key.startsWith('UNALLOCATED')) return -1
			return a.key.localeCompare(b.key)
		})
	})

	// Group selected items by route for TOS builder sidebar
	const selectedRouteGroups = computed((): SelectedRouteGroup[] => {
		const selected = flaggedItems.value.filter(i => selectedItemKeys.value.has(i.itemKey) && i.sourceDepot)
		const routeMap = new Map<string, FlaggedItem[]>()

		for (const item of selected) {
			const routeKey = `${item.sourceDepot}|${item.requestDepot}`
			if (!routeMap.has(routeKey)) {
				routeMap.set(routeKey, [])
			}
			routeMap.get(routeKey)!.push(item)
		}

		const groups: SelectedRouteGroup[] = []
		for (const [routeKey, items] of routeMap.entries()) {
			const [sourceDepot, destinationDepot] = routeKey.split('|')
			const totalQuantity = items.reduce((sum, i) => sum + (i.item.allocation_quantity || i.item.quantity_requested), 0)
			const totalWeight = items.reduce((sum, i) => sum + (i.item.item_weight || 0), 0)
			const contracts = [...new Set(items.map(i => i.requestContractNumber))].sort()

			groups.push({
				routeKey,
				sourceDepot,
				destinationDepot,
				items: items.sort((a, b) => a.item.item_code.localeCompare(b.item.item_code)),
				totalQuantity,
				totalWeight,
				contracts,
				stockCheckStatus: depotOverallStockCheckStatus.value.get(sourceDepot) || null
			})
		}

		return groups.sort((a, b) => a.sourceDepot.localeCompare(b.sourceDepot) || a.destinationDepot.localeCompare(b.destinationDepot))
	})

	const selectedTotalQuantity = computed(() => {
		const selected = flaggedItems.value.filter(i => selectedItemKeys.value.has(i.itemKey))
		return selected.reduce((sum, i) => sum + (i.item.allocation_quantity || i.item.quantity_requested), 0)
	})

	const selectedTotalWeight = computed(() => {
		const selected = flaggedItems.value.filter(i => selectedItemKeys.value.has(i.itemKey))
		return selected.reduce((sum, i) => sum + (i.item.item_weight || 0), 0)
	})

	const selectedContractGroups = computed(() => {
		const selected = flaggedItems.value.filter(i => selectedItemKeys.value.has(i.itemKey))
		if (selected.length === 0) return []

		const requestMap = new Map<string, {
			contract_number: string
			shortage_request_id: string
			selected_items: number
			total_items: number
			already_covered: number
			covered_after_tos: number
			fulfillment_percentage: number
		}>()

		for (const item of selected) {
			if (!requestMap.has(item.requestId)) {
				const request = props.requests.find(r => r.id === item.requestId)
				const totalItems = request?.items.length || 0
				const alreadyCovered = request?.items.filter(i =>
					i.fulfillment_status === 'pending-fulfillment' ||
					i.fulfillment_status === 'fulfilled'
				).length || 0

				requestMap.set(item.requestId, {
					contract_number: item.requestContractNumber,
					shortage_request_id: item.requestId,
					selected_items: 0,
					total_items: totalItems,
					already_covered: alreadyCovered,
					covered_after_tos: 0,
					fulfillment_percentage: 0
				})
			}
			requestMap.get(item.requestId)!.selected_items++
		}

		for (const contract of requestMap.values()) {
			const wouldBeFulfilled = contract.already_covered + contract.selected_items
			contract.covered_after_tos = Math.min(contract.total_items, wouldBeFulfilled)
			contract.fulfillment_percentage = contract.total_items > 0
				? Math.min(100, Math.round((wouldBeFulfilled / contract.total_items) * 100))
				: 0
		}

		return Array.from(requestMap.values())
	})

	const allDepotsExpanded = computed(() => {
		if (sourceDepotGroups.value.length === 0) return false
		return sourceDepotGroups.value.every(d => expandedDepots.value.has(d.depotName))
	})

	// Selection helpers
	const selectableItemsInGroup = (group: ItemGroup): FlaggedItem[] => {
		return group.items.filter(i => !!i.sourceDepot)
	}

	const isGroupFullySelected = (group: ItemGroup): boolean => {
		const selectable = selectableItemsInGroup(group)
		if (selectable.length === 0) return false
		return selectable.every(i => selectedItemKeys.value.has(i.itemKey))
	}

	const toggleGroupSelection = (group: ItemGroup) => {
		const selectable = selectableItemsInGroup(group)
		if (selectable.length === 0) return

		const allSelected = isGroupFullySelected(group)
		const newSet = new Set(selectedItemKeys.value)

		for (const item of selectable) {
			if (allSelected) {
				newSet.delete(item.itemKey)
			} else {
				newSet.add(item.itemKey)
			}
		}

		selectedItemKeys.value = newSet
		saveSelections()
	}

	const toggleItemSelection = (itemKey: string) => {
		const newSet = new Set(selectedItemKeys.value)
		if (newSet.has(itemKey)) {
			newSet.delete(itemKey)
		} else {
			newSet.add(itemKey)
		}
		selectedItemKeys.value = newSet
		saveSelections()
	}

	const clearFilters = () => {
		searchFilter.value = ''
		dueInFilter.value = 'all'
		targetDeliveryFilter.value = 'all'
	}

	const saveSelections = () => {
		try {
			localStorage.setItem('tosBuilderSelections', JSON.stringify(Array.from(selectedItemKeys.value)))
		} catch (error) {
			console.error('Error saving selections:', error)
		}
	}

	const toggleGroup = (key: string) => {
		const newSet = new Set(expandedGroups.value)
		if (newSet.has(key)) {
			newSet.delete(key)
		} else {
			newSet.add(key)
		}
		expandedGroups.value = newSet
	}

	const clearSelection = () => {
		selectedItemKeys.value = new Set()
		saveSelections()
	}

	// Depot expansion
	const toggleDepot = (depotName: string) => {
		const newSet = new Set(expandedDepots.value)
		if (newSet.has(depotName)) {
			newSet.delete(depotName)
		} else {
			newSet.add(depotName)
		}
		expandedDepots.value = newSet
	}

	const toggleAllDepots = () => {
		if (allDepotsExpanded.value) {
			expandedDepots.value = new Set()
		} else {
			const allDepotNames = sourceDepotGroups.value.map(d => d.depotName)
			expandedDepots.value = new Set(allDepotNames)
		}
	}

	// Stock check: flag all items in a depot as 'stock_check_pending'
	const runStockCheckForDepot = async (depotName: string, skipConfirm = false) => {
		const items = flaggedItems.value.filter(i => i.sourceDepot === depotName)
		if (items.length === 0) return

		// Prompt user if any items are already checked
		if (!skipConfirm) {
			const alreadyChecked = items.filter(i => i.item.stock_check_status === 'checked' || i.item.stock_check_status === 'manually_checked')
			if (alreadyChecked.length > 0) {
				depotToReset.value = depotName
				return
			}
		}

		await performStockCheckUpdate(depotName, items)
	}

	const confirmDepotReset = async () => {
		if (depotToReset.value) {
			const depotName = depotToReset.value
			const items = itemsInDepotToReset.value
			depotToReset.value = null
			await performStockCheckUpdate(depotName, items)
		}
	}

	const performStockCheckUpdate = async (depotName: string, items: FlaggedItem[]) => {
		const newChecking = new Set(checkingDepots.value)
		newChecking.add(depotName)
		checkingDepots.value = newChecking

		try {
			// Update each item to 'stock_check_pending' via API
			const updatePromises = items
				.filter(i => i.item.id)
				.map(i => stockService.updateShortageRequestItem(i.item.id!, {
					stock_check_status: 'stock_check_pending'
				}))

			await Promise.allSettled(updatePromises)

			// Update local state
			for (const item of items) {
				item.item.stock_check_status = 'stock_check_pending'
			}

			toaster.success({ text: `${items.length} item(s) at ${depotName} flagged as pending` })
		} catch (error) {
			console.error('Error flagging stock check:', error)
			toaster.error({ text: `Failed to flag items at ${depotName}` })
		} finally {
			const newChecking2 = new Set(checkingDepots.value)
			newChecking2.delete(depotName)
			checkingDepots.value = newChecking2
		}
	}

	// Mark a single item as manually checked
	const markItemManuallyChecked = async (item: FlaggedItem) => {
		if (!item.item.id) return

		const newSaving = new Set(savingItems.value)
		newSaving.add(item.item.id)
		savingItems.value = newSaving

		try {
			await stockService.markManuallyChecked(item.item.id)

			item.item.stock_check_status = 'manually_checked'
			item.item.stock_checked_at = new Date().toISOString()

			toaster.success({ text: `${item.item.item_code} marked as manually checked` })
		} catch (error) {
			console.error('Error marking item as manually checked:', error)
			toaster.error({ text: `Failed to mark ${item.item.item_code} as manually checked` })
		} finally {
			const newSaving2 = new Set(savingItems.value)
			newSaving2.delete(item.item.id)
			savingItems.value = newSaving2
		}
	}

	// Stock check: reset ALL depots to check pending (after confirmation)
	const confirmCheckAllDepots = async () => {
		showCheckAllModal.value = false
		const allDepots = sourceDepotGroups.value
		
		// Reset local state first for immediate UI feedback
		for (const group of allDepots) {
			for (const item of group.items) {
				item.item.stock_check_status = 'stock_check_pending'
			}
		}

		await Promise.allSettled(allDepots.map(d => runStockCheckForDepot(d.depotName, true)))
	}

	// Open create TOS modal
	const openCreateTosModal = (route: SelectedRouteGroup) => {
		const tosItems: TransferOfStockItem[] = route.items.map(i => ({
			item_code: i.item.item_code,
			description: i.item.description,
			quantity: i.item.allocation_quantity || i.item.quantity_requested,
			item_weight: i.item.item_weight,
			shortage_request_id: i.requestId,
			shortage_item_id: i.item.id,
			contract_number: i.requestContractNumber,
			destination_depot: i.requestDepot,
			required_by_date: i.item.required_by_date
		}))

		const totalQuantity = tosItems.reduce((sum, i) => sum + i.quantity, 0)
		const totalWeight = tosItems.reduce((sum, i) => sum + (i.item_weight || 0), 0)
		const shortageRequestIds = [...new Set(route.items.map(i => i.requestId))]

		const payload: TransferOfStockPayload = {
			source_depot: route.sourceDepot,
			destination_depot: route.destinationDepot,
			items: tosItems,
			shortage_request_ids: shortageRequestIds,
			user_notes: userNotes.value.trim() || undefined,
			total_quantity: totalQuantity,
			total_weight: totalWeight || undefined
		}

		selectedRouteForReview.value = route
		tosPayload.value = payload
		showTosModal.value = true
	}

	// TOS validation warnings with item details
	const getValidationWarnings = (route: SelectedRouteGroup) => {
		const warnings: Array<{ message: string; items?: FlaggedItem[]; type: 'stock-check' | 'allocation' }> = []
		
		// Check for items with 0 quantity
		const zeroQuantityItems = route.items.filter(i => {
			const qty = i.item.allocation_quantity || i.item.quantity_requested
			return !qty || qty <= 0
		})
		if (zeroQuantityItems.length > 0) {
			warnings.push({
				message: `${zeroQuantityItems.length} item(s) have zero or invalid quantity`,
				items: zeroQuantityItems,
				type: 'allocation'
			})
		}
		
		// Check for items without stock check
		const uncheckedItems = route.items.filter(i => !i.item.stock_check_status || (i.item.stock_check_status !== 'checked' && i.item.stock_check_status !== 'manually_checked'))
		if (uncheckedItems.length > 0) {
			warnings.push({
				message: `${uncheckedItems.length} item(s) have not been stock checked`,
				items: uncheckedItems,
				type: 'stock-check'
			})
		}
		
		// Check for items with insufficient stock
		const insufficientItems = route.items.filter(i => {
			const checkKey = `${i.sourceDepot}|${i.item.item_code}|${i.itemKey}`
			const result = stockCheckResults.value.get(checkKey)
			if (result && !result.is_sufficient) return true
			
			// Also check if stock_check_result is less than requested quantity
			const qty = i.item.allocation_quantity || i.item.quantity_requested
			if (i.item.stock_check_result !== null && i.item.stock_check_result !== undefined) {
				if (i.item.stock_check_result < qty) return true
			}
			
			return false
		})
		if (insufficientItems.length > 0) {
			warnings.push({
				message: `${insufficientItems.length} item(s) have insufficient stock at source depot`,
				items: insufficientItems,
				type: 'stock-check'
			})
		}
		
		// Check for items without target delivery dates
		const noTargetDeliveryDate = route.items.filter(i => !i.item.target_delivery_date)
		if (noTargetDeliveryDate.length > 0) {
			warnings.push({
				message: `${noTargetDeliveryDate.length} item(s) missing target delivery date`,
				items: noTargetDeliveryDate,
				type: 'allocation'
			})
		}
		
		// Check for overdue items
		const now = Date.now()
		const overdueItems = route.items.filter(i => {
			const dueDate = new Date(i.item.required_by_date).getTime()
			return dueDate < now
		})
		if (overdueItems.length > 0) {
			warnings.push({
				message: `${overdueItems.length} item(s) are overdue`,
				items: overdueItems,
				type: 'allocation'
			})
		}
		
		// Check if no stock check performed at all
		if (!route.stockCheckStatus) {
			warnings.push({ message: 'No stock check has been performed for this route', type: 'stock-check' })
		}
		
		return warnings
	}

	const closeModal = () => {
		showTosModal.value = false
		selectedRouteForReview.value = null
		tosPayload.value = null
		userNotes.value = ''
		creatingTos.value = false
	}

	
	const createTos = async () => {
		if (!tosPayload.value) return
		
		creatingTos.value = true
		const createdTosItemKeys = new Set(
			selectedRouteForReview.value?.items.map((item) => item.itemKey) || []
		)
		try {
			// Include the latest optional note entered in the review modal.
			tosPayload.value.user_notes = userNotes.value.trim() || undefined
			
			const response = await stockService.createTransferOfStock(tosPayload.value)

			// Debug logging
			console.log('Full TOS Response:', JSON.stringify(response, null, 2))
			console.log('response.data:', response?.data)
			console.log('response.data.data:', response?.data?.data)
			console.log('response.data.data.transfer_number:', response?.data?.data?.transfer_number)

			// Extract transfer_number from response (handle different response structures)
			const transferNumber = response?.data?.data?.transfer_number || response?.data?.transfer_number || response?.transfer_number || null
		
			console.log('Extracted Transfer Number:', transferNumber)

			// Bulk update all items with pending-fulfillment status and tos_no
			const itemsToUpdate = tosPayload.value.items
				.filter(i => i.shortage_item_id)
				.map(i => ({
					id: i.shortage_item_id!,
					fulfillment_status: 'pending-fulfillment',
					tos_no: transferNumber
				}))
			
			if (itemsToUpdate.length > 0) {
				console.log('Bulk updating items:', itemsToUpdate)
				try {
					await stockService.bulkUpdateItemStatus(itemsToUpdate)
				} catch (updateError) {
					console.error('Error updating item statuses:', updateError)
					toaster.warning({ text: `TOS created (${transferNumber}), but failed to update item statuses` })
				}
			}

			toaster.success({ text: `Transfer of Stock created successfully: ${transferNumber || 'TOS'}` })
			
			// Clear only the items included in the TOS just created.
			const remainingSelections = new Set(selectedItemKeys.value)
			for (const itemKey of createdTosItemKeys) {
				remainingSelections.delete(itemKey)
			}
			selectedItemKeys.value = remainingSelections
			saveSelections()
			
			// Emit updated event to refresh parent data
			emit('updated')
			
			closeModal()
		} catch (error) {
			console.error('Error creating TOS:', error)
			toaster.error({ text: 'Failed to create Transfer of Stock' })
		} finally {
			creatingTos.value = false
		}
	}

	const viewShortageRequest = (requestId: string) => {
		// Emit event to parent to focus on the specific shortage request
		emit('focus-request', requestId)
	}

	const getRequestStatus = (requestId: string): string => {
		const request = props.requests.find(r => r.id === requestId)
		return request?.status || 'unknown'
	}

	const getRequestStatusClass = (status: string): string => {
		switch (status) {
			case 'pending':
				return 'bg-yellow-100 text-yellow-800'
			case 'rejected':
				return 'bg-red-100 text-red-800'
			case 'completed':
				return 'bg-blue-100 text-blue-800'
			default:
				return 'bg-gray-100 text-gray-600'
		}
	}

	const getDaysClass = (days: number): string => {
		if (days < 0) return 'text-red-600'
		if (days <= 2) return 'text-orange-500'
		if (days <= 7) return 'text-yellow-600'
		return 'text-green-600'
	}

	const formatShortDate = (dateString: string): string => {
		if (!dateString) return '—'
		const date = new Date(dateString)
		return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
	}

	</script>

<style scoped>
	table {
		border-collapse: separate;
		border-spacing: 0;
	}
	
	.stock-check-table th:nth-child(1),
	.stock-check-table td:nth-child(1) {
		width: 120px;
	}
	
	.stock-check-table td:nth-child(1) {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	
	.stock-check-table th:nth-child(2),
	.stock-check-table td:nth-child(2) {
		width: 250px;
	}
	
	.stock-check-table td:nth-child(2) {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	
	.stock-check-table th:nth-child(3),
	.stock-check-table td:nth-child(3) {
		max-width: 100px;
		min-width: 80px;
	}
	
	.stock-check-table th:nth-child(4),
	.stock-check-table td:nth-child(4) {
		max-width: 80px;
		min-width: 60px;
		text-align: center;
	}
	
	.stock-check-table th:nth-child(5),
	.stock-check-table td:nth-child(5) {
		max-width: 100px;
		min-width: 80px;
		text-align: center;
	}
	
	.stock-check-table th:nth-child(6),
	.stock-check-table td:nth-child(6) {
		max-width: 140px;
		min-width: 120px;
		text-align: right;
	}
</style>
